import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  BookmarkAdd as BookmarkAddIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Sync as SyncIcon,
  EditNote as EditNoteIcon,
} from "@mui/icons-material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { createItineraryCard, updateItineraryCard } from "../../api/itineraryCardsAPI";
import { isTitleInDb } from "../../pages/createItinerary/createInquiryCalculation";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
];

const DetailedItineraryModal = ({
  open,
  onClose,
  dayIndex,
  dayTitle: propDayTitle = "",
  currentDetails,
  onSave,
  onTitleChange,
  selectedDestination = "",
  storedItineraries = [],
  onCardCreated,
  onCardUpdated,
  showSnackbar,
}) => {
  const [dayTitle, setDayTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [paragraphs, setParagraphs] = useState([""]);
  const [selectedStoreItinerary, setSelectedStoreItinerary] = useState(null);
  const [saveToDbChecked, setSaveToDbChecked] = useState(false);
  const [updateDbChecked, setUpdateDbChecked] = useState(false);
  const [isSavingDb, setIsSavingDb] = useState(false);
  const [dbSavedSuccess, setDbSavedSuccess] = useState(false);
  const [dbUpdatedSuccess, setDbUpdatedSuccess] = useState(false);
  const [activeDbCard, setActiveDbCard] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Clean string helper for checking DB matches
  const cleanStr = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/^(?:day\s*\d+\s*[:\-–]?\s*|\d+[\.\)]\s*)/i, "")
      .replace(/[^a-z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  useEffect(() => {
    if (open) {
      const initialTitle = propDayTitle || currentDetails?.dayTitle || (dayIndex !== null ? `Day ${dayIndex + 1}` : "");
      setDayTitle(initialTitle);
      setDestination(selectedDestination || "Sikkim");

      if (currentDetails && currentDetails.para && currentDetails.para.length > 0) {
        setParagraphs(currentDetails.para);
      } else {
        setParagraphs([""]);
      }
      setSelectedStoreItinerary(null);
      setDbSavedSuccess(false);
      setDbUpdatedSuccess(false);
      setErrorMessage("");
      setSaveToDbChecked(false);
      setUpdateDbChecked(false);

      // Find initial card from DB
      const matched = (storedItineraries || []).find(c =>
        (currentDetails?.cardId && c?._id === currentDetails.cardId) ||
        (currentDetails?.matchedCardTitle && cleanStr(c?.title) === cleanStr(currentDetails.matchedCardTitle)) ||
        (initialTitle && cleanStr(c?.title) === cleanStr(initialTitle))
      ) || null;
      setActiveDbCard(matched);
    }
  }, [open, currentDetails, propDayTitle, dayIndex, selectedDestination, storedItineraries]);

  // Handle loading itinerary from store template dropdown
  useEffect(() => {
    if (selectedStoreItinerary) {
      if (selectedStoreItinerary.title) {
        setDayTitle(selectedStoreItinerary.title);
      }
      if (selectedStoreItinerary.destination) {
        setDestination(selectedStoreItinerary.destination);
      }
      if (selectedStoreItinerary.description) {
        setParagraphs([selectedStoreItinerary.description]);
      }
      setActiveDbCard(selectedStoreItinerary);
      setDbSavedSuccess(false);
      setDbUpdatedSuccess(false);
      setErrorMessage("");
    }
  }, [selectedStoreItinerary]);

  // Find existing card in DB if it already exists by exact title
  const existingCard = useMemo(() => {
    if (!dayTitle || !dayTitle.trim()) return null;
    const target = cleanStr(dayTitle);
    if (!target) return null;
    return (storedItineraries || []).find((c) => cleanStr(c?.title) === target) || null;
  }, [dayTitle, storedItineraries]);

  // Target card is either direct title match OR the card that was loaded/matched initially
  const targetCard = existingCard || activeDbCard;

  // Check if current dayTitle is already stored in database
  const isAlreadyInDb = Boolean(targetCard || dbSavedSuccess || dbUpdatedSuccess);

  const handleAddParagraph = () => {
    setParagraphs([...paragraphs, ""]);
  };

  const handleRemoveParagraph = (index) => {
    if (paragraphs.length <= 1) {
      setParagraphs([""]);
      return;
    }
    setParagraphs(paragraphs.filter((_, i) => i !== index));
  };

  const handleParagraphChange = (index, value) => {
    const updated = [...paragraphs];
    updated[index] = value;
    setParagraphs(updated);
  };

  // Direct save NEW to Database (MongoDB itinerarycards collection)
  const handleSaveToDbDirect = async () => {
    const titleToSave = dayTitle.trim();
    if (!titleToSave) {
      setErrorMessage("Please enter an Itinerary Title before saving to the database.");
      return;
    }
    const destToSave = (destination || selectedDestination || "Sikkim").trim();
    const validParas = paragraphs.filter((p) => p && p.trim() !== "");
    if (!validParas.length) {
      setErrorMessage("Please enter some itinerary description before saving to the database.");
      return;
    }

    setIsSavingDb(true);
    setErrorMessage("");
    try {
      const fullDesc = validParas.join("<p><br></p>");
      const res = await createItineraryCard({
        title: titleToSave,
        destination: destToSave,
        description: fullDesc,
      });

      if (res && (res.success || res._id || res.data)) {
        const savedCard = res.data || res;
        setDbSavedSuccess(true);
        setActiveDbCard(savedCard);
        if (onCardCreated) {
          onCardCreated(savedCard);
        }
        if (showSnackbar) {
          showSnackbar(`Saved "${titleToSave}" to Itinerary Library in DB!`, "success");
        }

        const detailedData = {
          para: validParas,
          dayTitle: titleToSave,
          isAutoPopulated: true,
          isManualEdited: false,
          matchedCardTitle: titleToSave,
          cardId: savedCard._id,
        };
        if (onSave) {
          onSave(dayIndex, detailedData);
        }
        if (onTitleChange) {
          onTitleChange(dayIndex, titleToSave);
        }
      } else {
        setErrorMessage(res?.message || "Failed to save itinerary card to database.");
      }
    } catch (err) {
      console.error("Error saving itinerary card:", err);
      setErrorMessage(
        err.response?.data?.message || err.message || "Failed to save itinerary card to database."
      );
    } finally {
      setIsSavingDb(false);
    }
  };

  // Direct UPDATE existing card in Database (MongoDB itinerarycards collection)
  const handleUpdateDbDirect = async () => {
    const cardToUpdate = targetCard;
    if (!cardToUpdate?._id) return;
    const titleToSave = dayTitle.trim();
    if (!titleToSave) {
      setErrorMessage("Please enter an Itinerary Title before updating.");
      return;
    }
    const destToSave = (destination || selectedDestination || "Sikkim").trim();
    const validParas = paragraphs.filter((p) => p && p.trim() !== "");
    if (!validParas.length) {
      setErrorMessage("Please enter some itinerary description before updating.");
      return;
    }

    setIsSavingDb(true);
    setErrorMessage("");
    try {
      const fullDesc = validParas.join("<p><br></p>");
      const res = await updateItineraryCard(cardToUpdate._id, {
        title: titleToSave,
        destination: destToSave,
        description: fullDesc,
      });

      if (res && (res.success || res.data || res._id)) {
        const updatedCard = res.data || res;
        setDbUpdatedSuccess(true);
        setActiveDbCard(updatedCard);
        if (onCardUpdated) {
          onCardUpdated(updatedCard);
        }
        if (showSnackbar) {
          showSnackbar(`Updated "${titleToSave}" in Database Library!`, "success");
        }

        const detailedData = {
          para: validParas,
          dayTitle: titleToSave,
          isAutoPopulated: true,
          isManualEdited: false,
          matchedCardTitle: titleToSave,
          cardId: updatedCard._id,
        };
        if (onSave) {
          onSave(dayIndex, detailedData);
        }
        if (onTitleChange) {
          onTitleChange(dayIndex, titleToSave);
        }
      } else {
        setErrorMessage(res?.message || "Failed to update itinerary card in database.");
      }
    } catch (err) {
      console.error("Error updating itinerary card:", err);
      setErrorMessage(
        err.response?.data?.message || err.message || "Failed to update database."
      );
    } finally {
      setIsSavingDb(false);
    }
  };

  // Primary Save Details handler (saves to inquiry day, and to DB if checked)
  const handleSave = async () => {
    const titleToSave = dayTitle.trim();
    const validParas = paragraphs.filter((p) => p && p.trim() !== "");
    let savedToDb = Boolean(dbSavedSuccess || dbUpdatedSuccess || targetCard);
    let cardId = targetCard?._id;

    // 1. If user checked update existing card in DB
    if (updateDbChecked && targetCard?._id && titleToSave && validParas.length > 0) {
      setIsSavingDb(true);
      try {
        const destToSave = (destination || selectedDestination || "Sikkim").trim();
        const fullDesc = validParas.join("<p><br></p>");
        const res = await updateItineraryCard(targetCard._id, {
          title: titleToSave,
          destination: destToSave,
          description: fullDesc,
        });
        if (res && (res.success || res.data)) {
          const updatedCard = res.data || res;
          cardId = updatedCard._id;
          if (onCardUpdated) onCardUpdated(updatedCard);
          if (showSnackbar) showSnackbar(`Updated "${titleToSave}" in Database Library!`, "success");
          savedToDb = true;
          setDbUpdatedSuccess(true);
          setActiveDbCard(updatedCard);
        }
      } catch (err) {
        console.error("Auto DB update failed:", err);
      } finally {
        setIsSavingDb(false);
      }
    }
    // 2. If user checked save new card in DB
    else if (saveToDbChecked && !targetCard && titleToSave && validParas.length > 0) {
      setIsSavingDb(true);
      try {
        const destToSave = (destination || selectedDestination || "Sikkim").trim();
        const fullDesc = validParas.join("<p><br></p>");
        const res = await createItineraryCard({
          title: titleToSave,
          destination: destToSave,
          description: fullDesc,
        });

        if (res && (res.success || res._id || res.data)) {
          savedToDb = true;
          const savedCard = res.data || res;
          cardId = savedCard._id;
          if (onCardCreated) onCardCreated(savedCard);
          if (showSnackbar) showSnackbar(`Saved "${titleToSave}" to Itinerary Library in DB!`, "success");
          setDbSavedSuccess(true);
          setActiveDbCard(savedCard);
        }
      } catch (err) {
        console.error("Auto DB save failed:", err);
      } finally {
        setIsSavingDb(false);
      }
    }

    const isUpdatedOrSynced = Boolean(dbUpdatedSuccess || updateDbChecked || (savedToDb && !targetCard));
    const detailedData = {
      para: validParas,
      dayTitle: titleToSave || undefined,
      isAutoPopulated: isUpdatedOrSynced ? true : Boolean(targetCard),
      isManualEdited: !isUpdatedOrSynced && Boolean(targetCard),
      matchedCardTitle: titleToSave || targetCard?.title || undefined,
      cardId: cardId,
    };

    if (onTitleChange && titleToSave) {
      onTitleChange(dayIndex, titleToSave);
    }
    if (onSave) {
      onSave(dayIndex, detailedData);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
            Detailed Itinerary for Day {dayIndex !== null ? dayIndex + 1 : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {dbUpdatedSuccess ? (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon fontSize="small" />}
                label="Updated in DB Library"
                sx={{ fontWeight: 600, fontSize: 12 }}
              />
            ) : isAlreadyInDb ? (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon fontSize="small" />}
                label="Saved in DB Library"
                sx={{ fontWeight: 600, fontSize: 12 }}
              />
            ) : (
              <Chip
                size="small"
                color="secondary"
                icon={<AutoAwesomeIcon fontSize="small" />}
                label="New Itinerary Day"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: 12, borderColor: "#a855f7", color: "#9333ea" }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ borderRadius: 1.5 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Title & Destination Inputs */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Day Itinerary Title / Route"
                placeholder="e.g. Gangtok to Pelling transfer via Ravangla"
                value={dayTitle}
                onChange={(e) => {
                  setDayTitle(e.target.value);
                  setDbSavedSuccess(false);
                  setDbUpdatedSuccess(false);
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Destination / State"
                placeholder="e.g. Sikkim"
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value);
                  setDbSavedSuccess(false);
                  setDbUpdatedSuccess(false);
                }}
              />
            </Grid>
          </Grid>

          {/* Quick Load from Existing Store */}
          {storedItineraries && storedItineraries.length > 0 && (
            <Box>
              <Autocomplete
                size="small"
                options={storedItineraries}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.title || "")}
                value={selectedStoreItinerary}
                onChange={(event, newValue) => {
                  setSelectedStoreItinerary(newValue);
                }}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Load template from Itinerary Library (optional)"
                    variant="outlined"
                    size="small"
                    placeholder="Choose an existing itinerary template..."
                  />
                )}
              />
            </Box>
          )}

          {/* UPDATE IN DB Banner: when card is already in DB */}
          {targetCard && !dbUpdatedSuccess && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
                bgcolor: "#fffbeb",
                color: "#92400e",
                border: "1px solid #fde68a",
                "& .MuiAlert-icon": { color: "#d97706" },
                display: "flex",
                alignItems: "center",
              }}
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="contained"
                  disabled={isSavingDb}
                  startIcon={
                    isSavingDb ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SyncIcon fontSize="small" />
                    )
                  }
                  onClick={handleUpdateDbDirect}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#d97706",
                    "&:hover": { bgcolor: "#b45309" },
                    boxShadow: "0 2px 8px rgba(217, 119, 6, 0.25)",
                    px: 1.5,
                  }}
                >
                  {isSavingDb ? "Updating..." : "Update in DB 🔄"}
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                <strong>Edit &amp; Update DB:</strong> You can edit the details below and click <strong>Update in DB 🔄</strong> to update the saved master card in your library.
              </Typography>
            </Alert>
          )}

          {/* SAVE NEW TO DB Banner: when card is brand new and NOT in DB */}
          {!isAlreadyInDb && !dbSavedSuccess && (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                bgcolor: "#f5f3ff",
                color: "#5b21b6",
                border: "1px solid #ddd6fe",
                "& .MuiAlert-icon": { color: "#7c3aed" },
                display: "flex",
                alignItems: "center",
              }}
              action={
                <Button
                  color="secondary"
                  size="small"
                  variant="contained"
                  disabled={isSavingDb || !dayTitle.trim()}
                  startIcon={
                    isSavingDb ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CloudUploadIcon fontSize="small" />
                    )
                  }
                  onClick={handleSaveToDbDirect}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#7c3aed",
                    "&:hover": { bgcolor: "#6d28d9" },
                    boxShadow: "0 2px 8px rgba(124, 58, 237, 0.25)",
                    px: 1.5,
                  }}
                >
                  {isSavingDb ? "Saving..." : "Save to DB Library 💾"}
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                <strong>New Itinerary!</strong> You can save this day to the DB Library so it auto-populates for future inquiries.
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 0.5 }} />

          {/* Detailed Paragraphs Rich Text Editor */}
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155" }}>
                Full Itinerary Description
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Use bullet points, bold headers, and notes (⚠️) as needed.
              </Typography>
            </Box>

            <Stack spacing={2}>
              {paragraphs.map((paragraph, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      minHeight: "140px",
                      ".ql-toolbar": {
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        borderColor: "#cbd5e1",
                        bgcolor: "#f8fafc",
                      },
                      ".ql-container": {
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                        borderColor: "#cbd5e1",
                        fontFamily: "inherit",
                        fontSize: "14px",
                      },
                      ".ql-editor": {
                        minHeight: "120px",
                        maxHeight: "350px",
                        overflowY: "auto",
                        lineHeight: 1.6,
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={paragraph}
                      onChange={(content) => {
                        handleParagraphChange(index, content);
                        setDbUpdatedSuccess(false);
                      }}
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  </Box>
                  {paragraphs.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveParagraph(index)}
                      sx={{ mt: 1, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}
                      size="small"
                      title="Remove paragraph"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddParagraph}
              sx={{ mt: 2, textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              Add Paragraph
            </Button>
          </Box>

          {/* Checkbox option to UPDATE existing DB card */}
          {targetCard && !dbUpdatedSuccess && (
            <Box sx={{ pt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateDbChecked}
                    onChange={(e) => setUpdateDbChecked(e.target.checked)}
                    color="warning"
                    sx={{ color: "#d97706", "&.Mui-checked": { color: "#b45309" } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#92400e" }}>
                    Also update the master Database Library card with these edits when saving details
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Checkbox option to SAVE NEW to DB */}
          {!isAlreadyInDb && !dbSavedSuccess && (
            <Box sx={{ pt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveToDbChecked}
                    onChange={(e) => setSaveToDbChecked(e.target.checked)}
                    color="secondary"
                    sx={{ color: "#9333ea", "&.Mui-checked": { color: "#7c3aed" } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
                    Also save this new itinerary to Database Library when saving details
                  </Typography>
                }
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>
          Cancel
        </Button>

        {/* Update in DB button */}
        {targetCard && !dbUpdatedSuccess && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={
              isSavingDb ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SyncIcon fontSize="small" />
              )
            }
            onClick={handleUpdateDbDirect}
            disabled={isSavingDb}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#f59e0b",
              color: "#d97706",
              "&:hover": { borderColor: "#d97706", bgcolor: "#fffbeb" },
            }}
          >
            {isSavingDb ? "Updating..." : "Update in DB 🔄"}
          </Button>
        )}

        {/* Save NEW to DB button */}
        {!isAlreadyInDb && !dbSavedSuccess && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={
              isSavingDb ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <BookmarkAddIcon fontSize="small" />
              )
            }
            onClick={handleSaveToDbDirect}
            disabled={isSavingDb || !dayTitle.trim()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#a855f7",
              color: "#9333ea",
              "&:hover": { borderColor: "#7c3aed", bgcolor: "#faf5ff" },
            }}
          >
            {isSavingDb ? "Saving..." : "Save to DB Library 💾"}
          </Button>
        )}

        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSavingDb}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            borderRadius: 1.5,
          }}
        >
          {isSavingDb ? "Saving..." : "Save Details"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailedItineraryModal;
